import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AddExternalBankAccountRequest,
  CreateStripeAccountRequest,
  GenerateOnboardingLinkRequest,
  GenerateOnboardingLinkResponse,
  StripeConnectedAccount,
  TaxFormsSessionResponse
} from '@umss/shared/data-access';

@Injectable({ providedIn: 'root' })
export class ContractorStripeService {
  private readonly baseUrl = '/api/contractors/me/stripe';

  private readonly http = inject(HttpClient);

  createAccount(
    body: CreateStripeAccountRequest = {}
  ): Observable<StripeConnectedAccount> {
    return this.http.post<StripeConnectedAccount>(
      `${this.baseUrl}/account`,
      body
    );
  }

  getAccountStatus(refresh = false): Observable<StripeConnectedAccount> {
    const options = refresh ? { params: { refresh: 'true' } } : {};
    return this.http.get<StripeConnectedAccount>(
      `${this.baseUrl}/account`,
      options
    );
  }

  generateOnboardingLink(
    body: GenerateOnboardingLinkRequest
  ): Observable<GenerateOnboardingLinkResponse> {
    return this.http.post<GenerateOnboardingLinkResponse>(
      `${this.baseUrl}/onboarding-link`,
      body
    );
  }

  addExternalBankAccount(
    body: AddExternalBankAccountRequest
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/external-bank-account`,
      body
    );
  }

  deleteAccount(contractorId: string): Observable<void> {
    return this.http.delete<void>(
      `/api/contractors/${contractorId}/stripe/account`
    );
  }

  /**
   * Requests a fresh AccountSession client secret for the tax-forms Connect.js
   * embedded components. Must be called fresh every time (initial load and
   * renewals) — never cache the result.
   */
  getTaxFormsSession(): Observable<TaxFormsSessionResponse> {
    return this.http.post<TaxFormsSessionResponse>(
      `${this.baseUrl}/tax-forms/session`,
      null
    );
  }

  sendTaxFormsEmailLink(taxYear?: number): Observable<{ message: string }> {
    const params =
      taxYear !== undefined ? { params: { taxYear: taxYear.toString() } } : {};
    return this.http.post<{ message: string }>(
      `${this.baseUrl}/tax-forms/email-link`,
      null,
      params
    );
  }
}
